"use client";

import { useEffect, useSyncExternalStore } from "react";

// L'état de l'événement de puzzles du moment, partagé par tout ce qui l'affiche (carte du
// tableau de bord, fenêtre de l'événement, salle de ligue, révélation d'une pièce).
//
// Pour ne rien demander inutilement : une seule requête par page, quel que soit le nombre
// de composants, et l'absence d'événement est retenue dix minutes pour l'onglet. Hors
// événement, ouvrir le tableau de bord ne coûte donc une requête que toutes les dix minutes.
const NONE_KEY = "believemy-avatar-event-none";
const NONE_TTL = 10 * 60 * 1000;

let state; // undefined : pas encore lu ; null : aucun événement ; sinon l'état.
let loading = null;
const listeners = new Set();

const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

// Pose un état reçu d'ailleurs (réponse d'un achat) sans refaire de requête.
export function setEventState(next) {
    state = next?.event ? next : null;
    try {
        if (state) sessionStorage.removeItem(NONE_KEY);
        else sessionStorage.setItem(NONE_KEY, String(Date.now()));
    } catch {}
    listeners.forEach((listener) => listener());
}

function knownAbsent() {
    try {
        return Date.now() - Number(sessionStorage.getItem(NONE_KEY) || 0) < NONE_TTL;
    } catch {
        return false;
    }
}

export function loadEvent({ fresh = false } = {}) {
    if (!fresh) {
        if (loading) return loading;
        if (knownAbsent()) {
            if (state === undefined) setEventState(null);
            return Promise.resolve();
        }
    }
    loading = fetch("/api/avatar/event", { cache: "no-store" })
        .then((response) => (response.ok ? response.json() : Promise.reject(new Error(String(response.status)))))
        .then(setEventState)
        .catch(() => {})
        .finally(() => {
            loading = null;
        });
    return loading;
}

// Rend undefined tant que rien n'est lu, null s'il n'y a pas d'événement, sinon l'état.
export default function useAvatarEvent(needed = true) {
    const current = useSyncExternalStore(subscribe, () => state, () => undefined);
    useEffect(() => {
        if (needed && state === undefined) loadEvent();
    }, [needed]);
    return current;
}
